/**
 * `/login` — see `web/shapes/login.md`.
 *
 * One form, no decoration. Everything on the screen is either a field, the submit,
 * the error or the locale switch.
 *
 * ── The password's life on this side of the wire ────────────────────────────
 *
 * It lives in one `useState` and in one request body. It is not put in a URL, not
 * logged, not stored, and not kept after the request resolves — the client half of
 * the rule `src/modules/auth/service.ts` states for the server. `autoComplete` is
 * `current-password` so a password manager fills it rather than a person retyping it
 * into a screen-shared window.
 *
 * ── Error copy is chosen by `code`, never by `message` ──────────────────────
 *
 * API_CONTRACT §5: "Client rẽ nhánh theo **code**, không bao giờ theo `message`."
 * §5.1 then decides whether the server's own sentence may be shown: user-safe codes
 * render it verbatim (it is already Vietnamese and already specific), the rest render
 * a generic sentence plus the correlation id, because the detail would help someone
 * probe system state.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import type { WorkforceApiError } from '../api/errors';
import { isWorkforceApiError } from '../api/errors';
import { useI18n } from '../i18n';
import { LOCALES, type Locale } from '../i18n/messages';
import { useSession } from '../auth/SessionProvider';
import { ScreenHeading } from '../shell/ScreenHeading';
import { StatusNote } from '../states/StatusNote';
import { ErrorCodeLine } from '../states/ErrorCodeLine';

const LOCALE_LABEL: Readonly<Record<Locale, 'locale.vi' | 'locale.en'>> = {
  vi: 'locale.vi',
  en: 'locale.en',
};

export function LoginScreen(): ReactNode {
  const { t, locale, setLocale } = useI18n();
  const { signIn } = useSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<WorkforceApiError | null>(null);

  // `FormEvent` is the type React's own `onSubmit` prop is declared with
  // (`FormEventHandler<T> = (event: FormEvent<T>) => void`), and @types/react 19
  // marks *both* names deprecated — so there is no non-deprecated spelling of a
  // synthetic submit event to move to. `SubmitEvent`, which the message suggests,
  // is the DOM event; this is not that. Disabled on the line rather than for
  // `web/**`, so the next real deprecation still reports.
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- upstream deprecation with no replacement for this usage
  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signIn({ username, password });
      // No `setPending(false)` on success: the session provider re-renders the tree
      // and this component unmounts. Setting state on the way out would be the
      // classic update-after-unmount warning, and there is nothing to re-enable.
      setPassword('');
    } catch (cause) {
      if (!isWorkforceApiError(cause)) throw cause;
      setError(cause);
      setPending(false);
    }
  }

  return (
    <div className="wp-centred">
      {/* The desk edge: the shell's own 240px rail, with the brand at the top of it
          and no nav. Paper fills the rest of the page (finish review, fix 6). */}
      <div className="wp-door__edge">
        <p className="wp-door__brand">{t('app.name')}</p>
      </div>

      {/* The form on the paper: no card, no shadow, left-aligned in a 400px column.
          The language switch sits under it, because it is about the reader rather
          than about the product. */}
      <div className="wp-centred__inner">
        <ScreenHeading>{t('login.title')}</ScreenHeading>
        <p className="wp-muted">{t('app.subtitle')}</p>

        <form className="wp-door__form" onSubmit={(event) => void onSubmit(event)} noValidate>
          {error === null ? null : <LoginError error={error} />}

          <div className="wp-field">
            <label htmlFor="login-username">{t('login.username')}</label>
            <input
              id="login-username"
              name="username"
              type="text"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
            />
          </div>

          <div className="wp-field">
            <label htmlFor="login-password">{t('login.password')}</label>
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>

          <button
            type="submit"
            className="wp-button wp-button--primary wp-button--block wp-door__submit"
            disabled={pending}
            data-testid="login-submit"
          >
            {pending ? t('login.submitting') : t('login.submit')}
          </button>
        </form>
        <div className="wp-toggle-group wp-door__foot" role="group" aria-label={t('locale.label')}>
          {LOCALES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              className="wp-toggle"
              aria-pressed={locale === candidate}
              onClick={() => {
                setLocale(candidate);
              }}
              data-testid={`locale-${candidate}`}
            >
              {t(LOCALE_LABEL[candidate])}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LoginError({ error }: { readonly error: WorkforceApiError }): ReactNode {
  const { t } = useI18n();
  const message = error.userSafe
    ? error.message
    : error.status === 0
      ? t('common.networkError')
      : t('common.unknownError');

  return (
    <StatusNote severity="critical" role="alert" title={message} testId="login-error">
      {error.retryAfterSeconds === null ? null : (
        <p>{t('common.retryAfter', { seconds: Math.ceil(error.retryAfterSeconds) })}</p>
      )}
      <ErrorCodeLine code={error.code} correlationId={error.correlationId} />
    </StatusNote>
  );
}
